/**
 * Auditorium (NGE-681) frontend types. Role values mirror the backend
 * `AuditoriumRole` enum (nexgen-services-nestjs). Promote to `nexgen-types` when
 * the shared enum lands so this local copy can be removed.
 */
export enum AuditoriumRole {
  MODERATOR = 'moderator',
  SPEAKER = 'speaker',
  AUDIENCE = 'audience',
}

export interface AuditoriumSessionMeta {
  pid: string;
  title: string;
  status: string;
  is_locked: boolean;
}

export interface AuditoriumJoinTokenResponse {
  roomId: string;
  token: string;
  role: AuditoriumRole;
  /**
   * Whether the session is this caller's to start and end — its creator, or a
   * co-moderator they named on it.
   *
   * Asked separately from `role` because the two answer different questions: every
   * member with no explicit role row resolves to MODERATOR so they can present in an
   * auditorium, so `role === 'moderator'` says nothing about owning the session.
   *
   * Optional: a server that predates this field sends nothing, and the client must
   * fall back to the role rather than lock everyone — including the creator — out.
   */
  can_moderate?: boolean;
  /**
   * Whether the caller may moderate the room — mute, lock, promote. Optional for the
   * same reason as `can_moderate`: a server that predates it sends nothing.
   */
  can_manage_room?: boolean;
  session: AuditoriumSessionMeta;
}

/** True when the role is allowed to publish audio/video/screen. */
export const isPublisherRole = (role: AuditoriumRole): boolean =>
  role === AuditoriumRole.MODERATOR || role === AuditoriumRole.SPEAKER;

/** Whether an attendee is an org member or a share-link guest (NGE-1078). */
export type AuditoriumAttendeeType = 'member' | 'guest';

/**
 * Where the attendee was found: `presence` = currently in the auditorium room,
 * `role` = only holds an explicit role row (promoted earlier, presence dropped).
 */
export type AuditoriumAttendeeSource = 'presence' | 'role';

/**
 * One row of the moderator panel's promote search (NGE-1078). Mirrors the backend
 * `AuditoriumAttendeeDto`; members and guests share this shape, so a guest row has
 * a name/email/avatar and no org identity.
 */
export interface AuditoriumAttendee {
  /** User PID for a member, guest PID for a guest. */
  pid: string;
  type: AuditoriumAttendeeType;
  name: string;
  email: string | null;
  avatar_url: string | null;
  current_role: AuditoriumRole;
  is_host: boolean;
  source: AuditoriumAttendeeSource;
}

export interface AuditoriumAttendeeSearchResult {
  items: AuditoriumAttendee[];
  total: number;
}

/** Result of a promote/demote, so the panel can update its roster in place. */
export interface AuditoriumRoleAssignment extends AuditoriumAttendee {
  previous_role: AuditoriumRole | null;
}

/** Human label for a role, used in outcome messages ("Ada is now a speaker"). */
export const roleNoun: Record<AuditoriumRole, string> = {
  [AuditoriumRole.MODERATOR]: 'moderator',
  [AuditoriumRole.SPEAKER]: 'speaker',
  [AuditoriumRole.AUDIENCE]: 'audience',
};

/** Session lifecycle as the backend reports it. A cancelled booking reads as `ended`. */
export type AuditoriumSessionStatusValue = 'scheduled' | 'live' | 'ended';

export type AuditoriumAccessMode = 'public' | 'invite_only';

/** A booking. Times are UTC ISO strings; the UI picks and renders in local time. */
export interface AuditoriumSchedule {
  session_pid: string;
  title: string;
  description: string | null;
  status: AuditoriumSessionStatusValue;
  access_mode: AuditoriumAccessMode;
  room_pid: string | null;
  scheduled_start_at: string | null;
  scheduled_end_at: string | null;
  /** IANA zone the organiser booked in; null for bookings made before it was captured. */
  scheduled_timezone: string | null;
  /** Null for ad-hoc sessions, which predate join tokens. */
  join_url: string | null;
  /** Guests emailed an invite by this booking; only on the schedule response. */
  invited_count?: number;
}

/** One row of a floor's session list. */
export interface AuditoriumFloorSession extends AuditoriumSchedule {
  room_name: string | null;
  host: { pid: string; name: string } | null;
  /** True when this caller may flip the session live right now. */
  can_start: boolean;
  /**
   * True when this caller may end the session that is running: its creator and the
   * co-moderators they named. See `AuditoriumJoinTokenResponse.can_moderate` for why
   * this cannot be read off the caller's role.
   *
   * Optional for the same reason as `can_moderate`: absent from a server that
   * predates it, and the client falls back instead of disabling the button for
   * everyone.
   */
  can_end?: boolean;
  /**
   * Whether this caller may delete the booking: its host, or an admin of its org. Only
   * ever true before it starts — a running session is ended, not deleted.
   *
   * Optional for the same reason as `can_end`: absent from a server that predates it.
   */
  can_delete?: boolean;
}

export interface AuditoriumFloorSessions {
  items: AuditoriumFloorSession[];
  /** Room PID → session PID for every session live on the floor right now. */
  live_sessions: Record<string, string>;
}

/** What a shared join link resolves to before the visitor has any token. */
export interface AuditoriumJoinInfo {
  session_pid: string;
  title: string;
  status: AuditoriumSessionStatusValue;
  is_locked: boolean;
  scheduled_start_at: string | null;
  scheduled_end_at: string | null;
  /** IANA zone the organiser booked in, shown next to the visitor's own local time. */
  scheduled_timezone: string | null;
  room_pid: string | null;
  floor_pid: string | null;
  host_pid: string | null;
  host_name: string | null;
  org_pid: string | null;
  org_slug: string | null;
  /** Set when the link was a guest's own emailed invite: their address, to pre-fill. */
  invited_email?: string | null;
}

export interface ScheduleAuditoriumInput {
  room_pid: string;
  title: string;
  description?: string;
  /** UTC ISO string. */
  scheduled_start_at: string;
  /** UTC ISO string. */
  scheduled_end_at?: string;
  /** IANA zone the organiser picked the times in. */
  scheduled_timezone?: string;
  access_mode?: AuditoriumAccessMode;
  /**
   * Org members who hold moderator on this session alongside its creator, so any of
   * them can start it if the creator is away.
   */
  moderator_user_pids?: string[];
  /** Guests to email the join link to once the session is booked. */
  guest_emails?: string[];
}
